/**
 * Endpoints de **Mantenimiento → Categorías** (`/api/mantenimiento/categorias`).
 *
 * El listado acepta `page`, `per_page`, `search` y `estado`; la pantalla pide una
 * sola página amplia y filtra en el cliente porque el catálogo es corto.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  CategoriaGuardada,
  CategoriaPayload,
  CategoriasFiltros,
  CategoriasRespuesta,
} from './categorias.types';

export const categoriasKeys = {
  todas: ['mantenimiento', 'categorias'] as const,
  lista: (filtros: CategoriasFiltros) => ['mantenimiento', 'categorias', filtros] as const,
} as const;

/** Arma la cadena de consulta del listado. */
function cadenaDe(filtros: CategoriasFiltros): string {
  const parametros = new URLSearchParams();

  if (filtros.page) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));
  if (filtros.search) parametros.set('search', filtros.search);
  if (filtros.estado) parametros.set('estado', filtros.estado);

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const categoriasApi = {
  listar: (filtros: CategoriasFiltros = {}, signal?: AbortSignal): Promise<CategoriasRespuesta> =>
    apiClient.get<CategoriasRespuesta>(
      `/api/mantenimiento/categorias${cadenaDe(filtros)}`,
      { signal },
    ),
  crear: (payload: CategoriaPayload): Promise<CategoriaGuardada> =>
    apiClient.post<CategoriaGuardada>('/api/mantenimiento/categorias', { body: payload }),
  actualizar: (id: number, payload: CategoriaPayload): Promise<CategoriaGuardada> =>
    apiClient.put<CategoriaGuardada>(`/api/mantenimiento/categorias/${id}`, { body: payload }),
  cambiarEstado: (id: number, activa: boolean): Promise<CategoriaGuardada> =>
    apiClient.patch<CategoriaGuardada>(`/api/mantenimiento/categorias/${id}/estado`, {
      body: { activa },
    }),
};

/** Listado de categorías. */
export function useCategorias(filtros: CategoriasFiltros = {}) {
  return useQuery({
    queryKey: categoriasKeys.lista(filtros),
    queryFn: ({ signal }) => categoriasApi.listar(filtros, signal),
  });
}

/** Alta de una categoría. */
export function useCrearCategoria() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: categoriasApi.crear,
    onSuccess: () => cliente.invalidateQueries({ queryKey: categoriasKeys.todas }),
  });
}

/** Edición de una categoría. */
export function useActualizarCategoria() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CategoriaPayload }) =>
      categoriasApi.actualizar(id, payload),
    onSuccess: () => cliente.invalidateQueries({ queryKey: categoriasKeys.todas }),
  });
}

/** Activar/desactivar una categoría. */
export function useCambiarEstadoCategoria() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, activa }: { id: number; activa: boolean }) =>
      categoriasApi.cambiarEstado(id, activa),
    onSuccess: () => cliente.invalidateQueries({ queryKey: categoriasKeys.todas }),
  });
}
